import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';
import { useExpenseByIdQuery } from '../../../__generated__/graphql';
import { collectFormDataExtraFields } from '../../../components/ExtraFields/logic';
import {
  ExtraFieldsSupportedObject,
  useObjectExtraFields,
} from '../../../components/ExtraFields';
import { validateExpense } from '../expenseValidation';
import {
  useExpenseFormState,
  useExpenseDropdownOptions,
  useExpenseSubmit,
  useMerchantField,
  useCarMileageAddresses,
  useCarMileageDistance,
  useCarMileageFormInitialization,
} from './hooks';
import {
  DynamicExpenseForm,
  SharedFormButtons,
  ExpenseFormWrapper,
  CarMileageMapSection,
  CarMileageAddressInputs,
  CarMileageDistanceDisplay,
} from './components';
import { ExpenseWrapperProps } from './types';
import {
  MILEAGE_RECORD_TYPE_KEY,
  AUTO_SAVE_DELAY,
  type MileageFormData,
  findTravelExpenseItem,
} from './carMileageHelpers';
import * as yup from 'yup';
import './ExpenseSaveModal.css';
import { ExpenseRecordType } from '../types';

export { MILEAGE_RECORD_TYPE_KEY };

type FormValues = Record<string, unknown>;

type Props = ExpenseWrapperProps;

const CarMileageFormContent: React.FC<Props> = ({
  onSave,
  onCancel,
  expenseGroupId,
  expenseId,
  loading = false,
  expenseFormData,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext<FormValues>();

  const {
    formData,
    setFormData,
    handleInputChange,
    isUpdatingExpense,
    hasLoadedInitialData,
    setHasLoadedInitialData,
    hasDataChanged,
    autoSaveExpense,
    updateExpenseWithMerchant,
    lastSavedDataRef,
  } = useExpenseFormState<MileageFormData>({
    expenseId: expenseId || null,
    initialData: {
      description: '',
      expenseDate: new Date(),
      expenseGroupId: expenseGroupId || '',
      projectId: '',
      categoryId: '',
      merchantId: '',
      merchantName: '',
    },
  });

  const [customMerchantOptions, setCustomMerchantOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const allFormValuesWatched = watch();
  const allFormValuesRef = useRef(allFormValuesWatched);
  allFormValuesRef.current = allFormValuesWatched;

  const { handleCreateMerchant } = useMerchantField({
    onMerchantChange: (merchantId: string) =>
      handleInputChange('merchantId', merchantId),
    updateExpenseWithMerchant,
    setMerchantOptions: setCustomMerchantOptions,
    merchantOptions: customMerchantOptions,
  });

  const {
    expenseGroupOptions,
    projectOptions,
    categoryOptions,
    merchantOptions,
  } = useExpenseDropdownOptions(customMerchantOptions, expenseFormData);

  const { getFormFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.Expense,
  );

  const { data: expenseData } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const travelItem = useMemo(
    () => findTravelExpenseItem(expenseData?.expense?.expenseItems),
    [expenseData?.expense?.expenseItems],
  );

  const [travelExpenseItemId, setTravelExpenseItemId] = React.useState<
    string | null
  >(null);
  const [kmDistance, setKmDistance] = React.useState<number | null>(null);
  const [distanceSyncInProgress, setDistanceSyncInProgress] =
    React.useState<boolean>(false);

  const {
    addressFrom,
    addressTo,
    startCoords,
    endCoords,
    isUpdatingExpenseItem,
    setAddressFrom,
    setAddressTo,
    geocodeAddress,
  } = useCarMileageAddresses({
    travelExpenseItemId,
    expenseId: expenseId ?? undefined,
    hasLoadedInitialData,
    initialAddressFrom: (travelItem?.flair__Address_From__c ?? undefined) as
      | string
      | undefined,
    initialAddressTo: (travelItem?.flair__Address_To__c ?? undefined) as
      | string
      | undefined,
  });

  const {
    kmDistance: polledKmDistance,
    distanceSyncInProgress: polledDistanceSyncInProgress,
  } = useCarMileageDistance({
    expenseId: expenseId ?? undefined,
    travelExpenseItemId,
    hasLoadedInitialData,
    initialKmDistance: travelItem?.flair__Km_Distance__c ?? null,
    initialDistanceSyncInProgress:
      travelItem?.flair__Distance_Sync_In_Progress__c || false,
  });

  React.useEffect(() => {
    if (polledKmDistance !== null && polledKmDistance !== kmDistance) {
      setKmDistance(polledKmDistance);
    }
  }, [polledKmDistance, kmDistance]);

  React.useEffect(() => {
    if (polledDistanceSyncInProgress !== distanceSyncInProgress) {
      setDistanceSyncInProgress(polledDistanceSyncInProgress);
    }
  }, [polledDistanceSyncInProgress, distanceSyncInProgress]);

  useCarMileageFormInitialization({
    expenseId: expenseId ?? undefined,
    hasLoadedInitialData,
    setFormData,
    setHasLoadedInitialData,
    setTravelExpenseItemId,
    setAddressFrom: (address) => setAddressFrom(address ?? ''),
    setAddressTo: (address) => setAddressTo(address ?? ''),
    setKmDistance,
    setDistanceSyncInProgress,
    geocodeAddress,
    lastSavedDataRef: lastSavedDataRef as React.MutableRefObject<{
      formData: MileageFormData;
      extraFieldsString: string;
    } | null>,
  });

  const isLoading = loading || isUpdatingExpense;

  const validateExpenseCallback = useCallback(() => {
    const allFormValues = watch();
    const formDataWithExtraFields = { ...formData, ...allFormValues };
    return validateExpense(formDataWithExtraFields, {
      getFormFields,
    });
  }, [formData, getFormFields, watch]);

  const { submitExpense, isSubmitting } = useExpenseSubmit({
    expenseId,
    onSave,
    successMessage: t('expenses.carMileageForm.expenseSubmittedSuccessfully'),
    errorMessage: t('expenses.carMileageForm.expenseSubmitFailed'),
    validateBeforeSubmit: validateExpenseCallback,
  });

  const extraFieldsString = useMemo(() => {
    if (!allFormValuesWatched) return '';
    const extraFields = collectFormDataExtraFields(allFormValuesWatched);
    return JSON.stringify(extraFields);
  }, [allFormValuesWatched]);

  useEffect(() => {
    if (!hasLoadedInitialData || !expenseId) {
      return;
    }

    const dataChanged = hasDataChanged(formData, extraFieldsString);

    if (!dataChanged) {
      return;
    }

    const timerId = setTimeout(async () => {
      await autoSaveExpense(allFormValuesRef.current, {});
    }, AUTO_SAVE_DELAY);

    return () => {
      clearTimeout(timerId);
    };
  }, [
    formData,
    expenseId,
    hasLoadedInitialData,
    extraFieldsString,
    hasDataChanged,
    autoSaveExpense,
  ]);

  const handleSubmit = async () => {
    await submitExpense();
  };

  return (
    <div className="car-mileage-form position-relative">
      <div>
        <Row>
          <Col md={6}>
            <CarMileageMapSection
              startCoords={startCoords}
              endCoords={endCoords}
            />
          </Col>

          <Col md={6} className="d-flex flex-column">
            <div className="flex-grow-1">
              <DynamicExpenseForm
                onValueChange={handleInputChange}
                displayFullDateTime={false}
                values={{
                  description: formData.description,
                  date: formData.expenseDate,
                  categoryId: formData.categoryId,
                  merchantId: formData.merchantId,
                  projectId: formData.projectId,
                  expenseGroupId: formData.expenseGroupId,
                }}
                options={{
                  categoryOptions,
                  projectOptions,
                  expenseGroupOptions,
                  merchantOptions,
                }}
                onCreateMerchant={handleCreateMerchant}
                showMerchant={true}
                categoryRequired={true}
                merchantRequired={false}
              />
            </div>

            <div className="mt-auto">
              <hr className="my-4 " />
              <CarMileageAddressInputs
                addressFrom={addressFrom}
                addressTo={addressTo}
                onAddressFromChange={setAddressFrom}
                onAddressToChange={setAddressTo}
              />
              <CarMileageDistanceDisplay
                kmDistance={kmDistance}
                distanceSyncInProgress={distanceSyncInProgress}
                isUpdatingExpense={isUpdatingExpense}
                isUpdatingExpenseItem={isUpdatingExpenseItem}
                expenseData={expenseData}
              />
            </div>
          </Col>
        </Row>

        <SharedFormButtons
          onCancel={onCancel}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          isUpdating={isUpdatingExpense || isUpdatingExpenseItem}
          submitDisabled={
            !startCoords || !endCoords || !!formData.expenseGroupId
          }
          submitDisabledTooltip={
            formData.expenseGroupId
              ? t('expenses.perDiemForm.submitDisabledTooltip')
              : undefined
          }
          showGroupWarning={!!formData.expenseGroupId}
        />
      </div>
    </div>
  );
};

export const CarMileageForm: React.FC<Props> = (props) => {
  const getDefaultValues = () => {
    return {};
  };

  const getValidationSchema = () => {
    return yup.object().shape({});
  };

  return (
    <ExpenseFormWrapper<FormValues>
      recordType={ExpenseRecordType.Mileage}
      wrapperProps={props}
      getDefaultValues={getDefaultValues}
      getValidationSchema={getValidationSchema}>
      <CarMileageFormContent {...props} />
    </ExpenseFormWrapper>
  );
};
